import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { ProviderUserDetail, useUserDetail } from "../../api/users";
import { useAuth } from "../../api/auth";
import { APP_FUNCTION_KEYS, formatAppFunctionKeyLabel, type AppFunctionKey, User } from "../../shared/types";

export default function UserDetails() {
    const { id } = useParams<{ id: string }>();
    const userId = id ? parseInt(id, 10) : undefined;

    return (
        <DashboardLayout>
            <ProviderUserDetail userId={Number.isFinite(userId) ? userId : undefined}>
                <UserDetailsContent />
            </ProviderUserDetail>
        </DashboardLayout>
    );
}

function UserDetailsContent() {
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const {
        user,
        errors,
        loading,
        saving,
        savingPassword,
        passwordError,
        fetchUser,
        saveUser,
        updatePassword,
    } = useUserDetail();

    const [snackbar, setSnackbar] = useState<string | null>(null);

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={() => navigate("/users")}
                >
                    Back
                </Button>
            </Stack>

            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchUser}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}

            {loading ? (
                <_DetailsSkeleton />
            ) : user ? (
                <StaggerContainer>
                    <StaggerItem>
                        <_ProfileForm
                            user={user}
                            saving={saving}
                            onSaved={() => setSnackbar("Profile saved.")}
                            saveUser={saveUser}
                        />
                    </StaggerItem>
                    <StaggerItem>
                        <_PermissionsSection
                            user={user}
                            isCurrentUser={Boolean(authUser && authUser.id === user.id)}
                            saving={saving}
                            saveUser={saveUser}
                            onSaved={() => setSnackbar("Permissions saved.")}
                        />
                    </StaggerItem>
                    <StaggerItem>
                        <_PasswordSection
                            savingPassword={savingPassword}
                            passwordError={passwordError}
                            onSaved={() => setSnackbar("Password updated.")}
                            updatePassword={updatePassword}
                        />
                    </StaggerItem>
                </StaggerContainer>
            ) : null}

            <Snackbar
                open={snackbar != null}
                autoHideDuration={4000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSnackbar(null)} variant="filled">
                    {snackbar}
                </Alert>
            </Snackbar>
        </Box>
    );
}

type SaveUserFn = (
    updates: Partial<Pick<User, "name" | "email">> & Record<string, unknown>
) => Promise<boolean>;

function _ProfileForm({
    user,
    saving,
    onSaved,
    saveUser,
}: {
    user: User;
    saving: boolean;
    onSaved: () => void;
    saveUser: SaveUserFn;
}) {
    const [name, setName] = useState(user.name);
    const [email, setEmail] = useState(user.email);

    useEffect(() => {
        setName(user.name);
        setEmail(user.email);
    }, [user.id, user.name, user.email]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const ok = await saveUser({ name, email });
        if (ok) onSaved();
    }

    return (
        <Card variant="outlined" component="form" onSubmit={handleSubmit}>
            <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Profile
                </Typography>
                <Grid
                    container
                    spacing={2}
                    sx={{ maxWidth: { xs: "100%" } }}
                >
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            autoComplete="email"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            autoComplete="name"
                        />
                    </Grid>
                    <Grid size={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            startIcon={saving ? undefined : <SaveRoundedIcon />}
                        >
                            {saving ? "Saving…" : "Save profile"}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _PasswordSection({
    savingPassword,
    passwordError,
    onSaved,
    updatePassword,
}: {
    savingPassword: boolean;
    passwordError: string | null;
    onSaved: () => void;
    updatePassword: (password: string, passwordConfirmation: string) => Promise<boolean>;
}) {
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [localError, setLocalError] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLocalError(null);
        if (password.length === 0) {
            setLocalError("Enter a new password.");
            return;
        }
        if (password !== confirm) {
            setLocalError("Passwords do not match.");
            return;
        }
        const ok = await updatePassword(password, confirm);
        if (ok) {
            setPassword("");
            setConfirm("");
            onSaved();
        }
    }

    return (
        <Card variant="outlined" component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <LockRoundedIcon color="action" fontSize="small" />
                    <Typography variant="h6" fontWeight={600}>
                        Password
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set a new password for this user. The current password is not required. Enter the
                    new password twice to confirm.
                </Typography>

                {(localError || passwordError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {localError ?? passwordError}
                    </Alert>
                )}

                <Grid
                    container
                    spacing={2}
                    sx={{ maxWidth: { xs: "100%" } }}
                >
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="New password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            size="small"
                            autoComplete="new-password"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Confirm password"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            fullWidth
                            size="small"
                            autoComplete="new-password"
                        />
                    </Grid>
                    <Grid size={12}>
                        <Button
                            type="submit"
                            variant="outlined"
                            disabled={savingPassword}
                            startIcon={savingPassword ? undefined : <LockRoundedIcon />}
                        >
                            {savingPassword ? "Updating…" : "Update password"}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _PermissionsSection({
    user,
    isCurrentUser,
    saving,
    saveUser,
    onSaved,
}: {
    user: User;
    isCurrentUser: boolean;
    saving: boolean;
    saveUser: SaveUserFn;
    onSaved: () => void;
}) {
    const [selected, setSelected] = useState<string[]>(
        Array.isArray(user.functions) ? user.functions : []
    );
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        setSelected(Array.isArray(user.functions) ? user.functions : []);
    }, [user.id, user.functions]);

    const hasChanges = (() => {
        const a = new Set(selected);
        const b = new Set(Array.isArray(user.functions) ? user.functions : []);
        if (a.size !== b.size) return true;
        for (const x of a) if (!b.has(x)) return true;
        return false;
    })();

    function toggle(key: string) {
        setSelected((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
    }

    async function handleSave() {
        setLocalError(null);
        const result = await saveUser({ functions: selected });
        if (result) onSaved();
        else setLocalError("Saving permissions failed. Please try again.");
    }

    return (
        <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                    Permissions
                    </Typography>
                    {!isCurrentUser && (
                        <Button
                            variant="contained"
                            size="small"
                            disabled={saving || !hasChanges}
                            onClick={handleSave}
                            startIcon={saving ? undefined : <SaveRoundedIcon fontSize="small" />}
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isCurrentUser
                        ? "Your permissions are shown here but cannot be changed on your own account."
                        : "Select the permissions you want this user to have."}
                </Typography>

                {localError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {localError}
                    </Alert>
                )}

                <Grid container spacing={1}>
                    {APP_FUNCTION_KEYS.map((key) => {
                        const checked = selected.includes(key);
                        return (
                            <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                disabled={isCurrentUser}
                                                size="small"
                                                onChange={() => toggle(key)}
                                            />
                                        }
                                        label={formatAppFunctionKeyLabel(key as AppFunctionKey)}
                                    />
                                </FormGroup>
                            </Grid>
                        );
                    })}
                </Grid>
            </CardContent>
        </Card>
    );
}

function _DetailsSkeleton() {
    return (
        <Card variant="outlined">
            <CardContent>
                <Skeleton variant="text" width={120} height={28} sx={{ mb: 2 }} />
                <Grid container spacing={2} maxWidth={480}>
                    <Grid size={12}>
                        <Skeleton variant="rounded" height={40} />
                    </Grid>
                    <Grid size={12}>
                        <Skeleton variant="rounded" height={40} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
